import { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import Navigation from '../../Navigation/Navigation';
import { ChildrenWaitingRoom } from '../ChildrenWaitingRoom';
import ParentAssignedAccounts from '../ParentAssignedAccounts/ParentAssignedAccounts';
import { useSafeTranslation } from '../../../hooks/useSafeTranslation';
import CustomChakraButton from '../../CustomChakraButton/CustomChakraButton';
import { useParentDashboardContextContext } from '../../../context/ParentDashboardContext';
import ParentAddKidPopUp from '../ParentMain/AddKidNotification/ParentAddKidPopUp/ParentAddKidPopUp';
import AddKidNotPossibleNotification from '../ParentMain/AddKidNotPossibleNotification/AddKidNotPossibleNotification';
import { ConsumerDto } from '../../../shared/type/parentDashboardContext.type';
import { useApi } from '../../../context/ApiProvider';
import { AxiosResponse } from 'axios';

interface NavOptionsInterface {
  link: string;
  name: string;
  component: React.ReactElement;
  isIndex: boolean;
}

export const ParentChildren = () => {
  const { t } = useSafeTranslation();
  const { parentDashboardContext, fetchParentDashboardContext } =
    useParentDashboardContextContext();
  const tenantId = parentDashboardContext?.tenants[0].tenantId;
  const [showAddKidPopUp, setShowAddKidPopUp] = useState(false);
  const [showAddKidNotPossiblePopUp, setShowAddKidNotPossiblePopUp] = useState(false);
  const [selectedPendingChild, setSelectedPendingChild] = useState<ConsumerDto | null>(null);
  const { apiParentConsumerCandidateController } = useApi();
  const [pendingChildren, setPendingChildren] = useState<ConsumerDto[] | null>(null);

  const fetchPendingChildren = async () => {
    try {
      const response: AxiosResponse<ConsumerDto[]> =
        await apiParentConsumerCandidateController('list').get(``);
      setPendingChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch consumer context:', error);
    }
  };

  const handleEditPendingChild = (child: ConsumerDto) => {
    setSelectedPendingChild(child);
  };

  const navOptions: NavOptionsInterface[] = [
    {
      link: 'assigned-accounts',
      name: t('parent_assigned_accounts.navigation.accepted'),
      component: <ParentAssignedAccounts />,
      isIndex: true
    },
    {
      link: 'waiting-room',
      name: t('parent_assigned_accounts.navigation.pending'),
      component: (
        <ChildrenWaitingRoom
          pendingChildren={pendingChildren}
          onEditClick={handleEditPendingChild}
        />
      ),
      isIndex: false
    }
  ];

  useEffect(() => {
    fetchPendingChildren();
  }, []);

  useEffect(() => {
    if (selectedPendingChild) {
      setShowAddKidPopUp(true);
    }
  }, [selectedPendingChild]);

  return (
    <section className="pt-spacing-xl pr-spacing-3xl pb-spacing-xl pl-spacing-3xl gap-spacing-4xl">
      <div className="pt-spacing-3xl flex flex-row justify-between items-center pb-spacing-3xl">
        <h1 className="p-0 m-0 font-semibold text-grayLight-900" style={{ fontSize: '30px' }}>
          {t('parent_assigned_accounts.title')}
        </h1>
        <div className="lg:w-auto w-full">
          <CustomChakraButton
            size="lg"
            hierarchy="primary"
            iconPosition="left"
            icon="plus"
            iconColor="#292524"
            buttonProps={{
              className: 'lg:w-auto w-full',
              onClick: () => {
                const isAutoChildrenRegisterEnabled = parentDashboardContext?.tenants?.find(
                  (tenant) => tenantId === tenant.tenantId
                )?.childrenRegistrationActive;
                if (isAutoChildrenRegisterEnabled) {
                  setShowAddKidPopUp(true);
                } else {
                  setShowAddKidNotPossiblePopUp(true);
                }
              }
            }}>
            {t('parent_assigned_accounts.add_child')}
          </CustomChakraButton>
        </div>
      </div>
      <div className="SingleCompany__contextContainer flex flex-col gap-spacing-xl">
        <Navigation navOptions={navOptions} />
        <Routes>
          {navOptions.map((item) => (
            <Route
              key={item.link}
              path={`/${item.link}`}
              element={item.component}
              index={item.isIndex}
            />
          ))}
        </Routes>
      </div>
      {showAddKidPopUp && (
        <ParentAddKidPopUp
          onClose={() => {
            setShowAddKidPopUp(false);
            fetchParentDashboardContext();
            setSelectedPendingChild(null);
            fetchPendingChildren();
          }}
          isOpen={showAddKidPopUp}
          pendingKid={selectedPendingChild}
        />
      )}
      {showAddKidNotPossiblePopUp && (
        <AddKidNotPossibleNotification
          tenantId={Number(tenantId)}
          onClose={() => {
            setShowAddKidNotPossiblePopUp(false);
          }}
          isOpen={showAddKidNotPossiblePopUp}
        />
      )}
    </section>
  );
};
